import React from 'react'
import useSWR from 'swr'
import { Link, useNavigate } from 'react-router-dom'

import List from '../ui/list'
import { getUsers, setAdminUserBan } from '../../api'
import useQuery from '../../context/useQuery'
import { useAuth } from '../../context/auth'
import { formatDateShort } from '../../lib/dates'
import { ReactComponent as MoneyIcon } from '../../static/images/money.svg'
import { useAlerts } from '../../context/alerts'

const UsersList = () => {
  const { user: authUser } = useAuth()
  const { addAlert } = useAlerts()
  const navigate = useNavigate()
  const params = useQuery()
  const invCount = Number(params.get('invCount') ?? 0)
  const page = Number(params.get('page') ?? 1)
  const emailFilter = params.get('email') ?? ''
  const limit = 10
  const listUrl = `/api/admin/users?page=${page}&limit=${limit}&invCount=${invCount}${
    emailFilter ? `&email=${encodeURIComponent(emailFilter)}` : ''
  }`
  const { data: users, isLoading, mutate } = useSWR(listUrl, getUsers)
  const [banningUserId, setBanningUserId] = React.useState<string | null>(null)
  const [emailInput, setEmailInput] = React.useState(emailFilter)

  React.useEffect(() => {
    setEmailInput(emailFilter)
  }, [emailFilter])

  const buildAdminQuery = (overrides: { invCount?: number; page?: number; email?: string }) => {
    const query = new URLSearchParams(params)
    query.delete('tab')
    query.set('invCount', String(overrides.invCount ?? invCount))
    query.set('page', String(overrides.page ?? 1))
    const email = overrides.email !== undefined ? overrides.email : emailFilter
    if (email.trim()) query.set('email', email.trim())
    else query.delete('email')
    return query.toString()
  }

  const navigateAdmin = (overrides: { invCount?: number; page?: number; email?: string }) => {
    navigate(`/admin?${buildAdminQuery(overrides)}`)
  }

  const handleBan = async (userId: string, email: string) => {
    const confirmed = window.confirm(
      `Ban ${email}? They will be logged out and cannot sign in until unbanned.`
    )
    if (!confirmed) return
    setBanningUserId(userId)
    try {
      await setAdminUserBan(userId, true)
      addAlert({ type: 'info', message: `${email} has been banned` })
      await mutate()
    } catch (error: any) {
      addAlert({ type: 'error', message: error?.message || 'Could not ban user' })
    } finally {
      setBanningUserId(null)
    }
  }

  const handleUnban = async (userId: string, email: string) => {
    setBanningUserId(userId)
    try {
      await setAdminUserBan(userId, false)
      addAlert({ type: 'info', message: `${email} has been unbanned` })
      await mutate()
    } catch (error: any) {
      addAlert({ type: 'error', message: error?.message || 'Could not unban user' })
    } finally {
      setBanningUserId(null)
    }
  }

  const headers = [
    { label: 'Email' },
    { label: 'Invoices' },
    { label: 'Emails Sent' },
    { label: 'Total $' },
    { label: 'Latest activity' },
    { label: 'Actions' },
  ]
  const rows: Array<any[]> = []

  if (isLoading) return <div>Loading...</div>
  if (users && users.data) {
    users.data.forEach((user: any) => {
      const latestActivity = user.latestActivityDate
        ? formatDateShort(user.latestActivityDate, authUser?.locale)
        : '—'
      const isBusy = banningUserId === user._id
      rows.push([
        <div key={user._id} className="d-flex align-items-center gap-1">
          {user.hasActiveSubscription && (
            <MoneyIcon width={16} height={16} aria-label="Active subscription" />
          )}
          <Link
            to={`/admin/users/${user._id}/invoices`}
            state={{ userEmail: user.email }}
            className={user.banned ? 'text-danger' : undefined}
          >
            {user.email}
          </Link>
        </div>,
        user.invoiceCount,
        user.emailsSentCount ?? 0,
        user.totalInvoiceAmount ?? 0,
        latestActivity,
        <div key={`${user._id}-actions`} className="d-flex gap-1">
          {user.banned ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={isBusy}
              onClick={() => handleUnban(user._id, user.email)}
            >
              Unban
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={isBusy}
              onClick={() => handleBan(user._id, user.email)}
            >
              Ban
            </button>
          )}
        </div>,
      ])
    })
  }

  const pagination = users?.pagination
  const currentPage = pagination?.page ?? page
  const totalPages = pagination?.totalPages ?? 1

  return (
    <>
      <p>
        <b>Total Users:</b> {pagination?.total ?? users?.data?.length ?? 0}
      </p>
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <div>
          <label className="d-block">Filter by email</label>
          <form
            className="d-flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              navigateAdmin({ email: emailInput, page: 1 })
            }}
          >
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Partial email…"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              style={{ minWidth: '14rem' }}
            />
            <button type="submit" className="btn btn-sm btn-primary">
              Search
            </button>
            {emailFilter && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => {
                  setEmailInput('')
                  navigateAdmin({ email: '', page: 1 })
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>
        <div>
          <label className="d-block">Minimum invoices</label>
          <select
            className="form-select form-select-sm"
            value={invCount}
            onChange={(e) => navigateAdmin({ invCount: Number(e.target.value), page: 1 })}
          >
            <option value={0}>All users (including 0)</option>
            <option value={1}>At least 1</option>
            <option value={5}>At least 5</option>
            <option value={10}>At least 10</option>
          </select>
        </div>
      </div>
      <List headers={headers} rows={rows} />
      <div className="d-flex align-items-center gap-2 mt-2">
        <button
          type="button"
          className="btn btn-light"
          disabled={currentPage <= 1}
          onClick={() => navigateAdmin({ page: currentPage - 1 })}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          className="btn btn-light"
          disabled={currentPage >= totalPages}
          onClick={() => navigateAdmin({ page: currentPage + 1 })}
        >
          Next
        </button>
      </div>
    </>
  )
}

export default UsersList
